/**
 * @file WithFEMSOverview.jsx
 * @description 공장에너지관리시스템(FEMS) 소개 페이지입니다.
 */

import {
  Activity,
  AlertTriangle,
  BarChart3,
  CloudSun,
  Droplets,
  Flame,
  Gauge,
  Leaf,
  LineChart,
  PlugZap,
  Wind,
  Zap,
} from 'lucide-react';

const CAPABILITIES = [
  {
    num: '01',
    category: 'METERING',
    title: '실시간 에너지 계측',
    Icon: PlugZap,
    desc: '전력·가스·스팀·용수 사용량을 설비, 라인, 공정 단위로 수집해 에너지 흐름을 실시간으로 가시화합니다.',
  },
  {
    num: '02',
    category: 'EPI ANALYTICS',
    title: '원단위·패턴 분석',
    Icon: LineChart,
    desc: '생산량과 에너지 사용량을 연결해 제품별·공정별 에너지 원단위와 피크 발생 패턴을 분석합니다.',
  },
  {
    num: '03',
    category: 'OPTIMIZATION',
    title: '피크·최적 제어',
    Icon: Gauge,
    desc: '목표 사용량과 부하 상태를 기준으로 이상 사용을 감지하고 피크 전력 억제와 운전 조건 최적화를 지원합니다.',
  },
  {
    num: '04',
    category: 'CARBON & ESG',
    title: '탄소·ESG 리포트',
    Icon: Leaf,
    desc: '에너지 비용, 온실가스 배출량, 절감 실적을 리포트로 제공해 탄소중립과 ESG 대응 기반을 만듭니다.',
  },
];

const ENERGY_METRICS = [
  { label: '오늘 전력 사용량', value: '12,840 kWh', tone: 'green' },
  { label: '현재 피크 부하', value: '742 kW', tone: 'amber' },
  { label: '에너지 원단위', value: '0.82 kWh/EA', tone: 'blue' },
  { label: '탄소 배출량', value: '5.6 tCO2e', tone: 'violet' },
];

const ENERGY_STREAMS = [
  { label: '전력', value: '61%', Icon: Zap },
  { label: '가스', value: '18%', Icon: Flame },
  { label: '스팀', value: '13%', Icon: Wind },
  { label: '용수', value: '8%', Icon: Droplets },
];

const PROCESS_STEPS = [
  { n: '01', title: '계측', desc: '계량기·PLC·센서에서 에너지 데이터 수집' },
  { n: '02', title: '저장', desc: '시계열 데이터와 생산 정보를 통합 저장' },
  { n: '03', title: '분석', desc: '공정별 원단위, 피크, 손실 구간 분석' },
  { n: '04', title: '제어', desc: '목표 부하와 운전 조건 기반 최적 제어' },
  { n: '05', title: '보고', desc: '비용·탄소·절감 실적 리포트 자동화' },
];

function FEMSEnergyFlowDiagram() {
  return (
    <svg viewBox="0 0 540 400" className="fems-flow-svg" aria-label="FEMS 에너지 흐름 관제 다이어그램">
      <defs>
        <linearGradient id="fems-factory-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#f5f8f4" />
        </linearGradient>
        <linearGradient id="fems-energy-line" x1="0" x2="1">
          <stop offset="0%" stopColor="#2f8f6b" stopOpacity="0.1" />
          <stop offset="55%" stopColor="#2f8f6b" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#c98a2b" stopOpacity="0.65" />
        </linearGradient>
      </defs>

      <rect x="10" y="16" width="520" height="368" fill="#fff" stroke="#c8bfb0" />

      <g className="fems-svg-sources">
        <rect x="38" y="54" width="112" height="48" fill="#fff" stroke="#2f8f6b" strokeWidth="1.5" />
        <path d="M60 65 51 81h8l-3 13 11-18h-9l2-11z" fill="none" stroke="#2f8f6b" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        <text x="82" y="83" fontSize="11" fontWeight="800" fill="#2f8f6b" fontFamily="inherit">POWER</text>

        <rect x="38" y="124" width="112" height="48" fill="#fff" stroke="#c98a2b" strokeWidth="1.5" />
        <path d="M58 156c-5-9 7-14 3-25 10 7 17 16 8 25-3 3-8 3-11 0z" fill="none" stroke="#c98a2b" strokeWidth="1.6" />
        <text x="84" y="153" fontSize="11" fontWeight="800" fill="#c98a2b" fontFamily="inherit">GAS</text>

        <rect x="38" y="194" width="112" height="48" fill="#fff" stroke="#6b7a90" strokeWidth="1.5" />
        <path d="M54 224c7-9 0-12 7-21M67 224c7-9 0-12 7-21" fill="none" stroke="#6b7a90" strokeWidth="1.5" strokeLinecap="round" />
        <text x="88" y="223" fontSize="11" fontWeight="800" fill="#6b7a90" fontFamily="inherit">STEAM</text>

        <rect x="38" y="264" width="112" height="48" fill="#fff" stroke="#2c6f9f" strokeWidth="1.5" />
        <path d="M58 279c8 10 8 10 8 16a8 8 0 0 1-16 0c0-6 0-6 8-16z" fill="none" stroke="#2c6f9f" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <text x="84" y="293" fontSize="11" fontWeight="800" fill="#2c6f9f" fontFamily="inherit">WATER</text>
      </g>

      <g>
        <path d="M150 78C190 78 196 126 230 126" stroke="url(#fems-energy-line)" strokeWidth="3" fill="none" strokeDasharray="7 9" />
        <path d="M150 148C185 148 202 150 230 150" stroke="url(#fems-energy-line)" strokeWidth="3" fill="none" strokeDasharray="7 9" />
        <path d="M150 218C190 218 198 174 230 174" stroke="url(#fems-energy-line)" strokeWidth="3" fill="none" strokeDasharray="7 9" />
        <path d="M150 288C192 288 196 204 230 198" stroke="url(#fems-energy-line)" strokeWidth="3" fill="none" strokeDasharray="7 9" />
        <circle r="3.5" fill="#2f8f6b">
          <animateMotion dur="2.4s" repeatCount="indefinite" path="M150 78C190 78 196 126 230 126" />
        </circle>
        <circle r="3.5" fill="#c98a2b">
          <animateMotion dur="2.8s" repeatCount="indefinite" path="M150 148C185 148 202 150 230 150" />
        </circle>
        <circle r="3.5" fill="#6b7a90">
          <animateMotion dur="2.6s" repeatCount="indefinite" path="M150 218C190 218 198 174 230 174" />
        </circle>
        <circle r="3.5" fill="#2c6f9f">
          <animateMotion dur="3s" repeatCount="indefinite" path="M150 288C192 288 196 204 230 198" />
        </circle>
      </g>

      <g className="fems-svg-factory">
        <path d="M230 106h122v126H230V106zm18 0V82h28v24h18V92h26v14" fill="url(#fems-factory-fill)" stroke="#2f8f6b" strokeWidth="2" />
        <rect x="250" y="132" width="28" height="34" fill="#fff" stroke="#2f8f6b" strokeWidth="1.2" />
        <rect x="302" y="132" width="28" height="34" fill="#fff" stroke="#c98a2b" strokeWidth="1.2" />
        <rect x="250" y="184" width="80" height="20" fill="#fff" stroke="#6b7a90" strokeWidth="1.2" />
        <text x="291" y="256" textAnchor="middle" fontSize="13" fontWeight="800" fill="#2f8f6b" fontFamily="inherit">FACTORY LOAD</text>
        <text x="291" y="274" textAnchor="middle" fontSize="9" fill="#857e6e" fontFamily="inherit">Line · Utility · Process</text>
        <circle cx="278" cy="132" r="4" fill="#2f8f6b">
          <animate attributeName="opacity" values="0.25;1;0.25" dur="1.8s" repeatCount="indefinite" />
        </circle>
        <circle cx="330" cy="184" r="4" fill="#c98a2b">
          <animate attributeName="opacity" values="0.25;1;0.25" dur="2.1s" repeatCount="indefinite" />
        </circle>
      </g>

      <g>
        <path d="M352 168H386" stroke="#bfb0a0" strokeWidth="1.5" strokeDasharray="5 7" />
        <circle r="3" fill="#2f8f6b">
          <animateMotion dur="2.3s" repeatCount="indefinite" path="M352 168H386" />
        </circle>
      </g>

      <g className="fems-svg-dashboard">
        <rect x="386" y="62" width="116" height="92" fill="#fff" stroke="#2f8f6b" strokeWidth="1.5" />
        <text x="404" y="88" fontSize="10" fontWeight="800" fill="#2f8f6b" fontFamily="inherit">ENERGY KPI</text>
        <rect x="404" y="104" width="70" height="8" fill="#2f8f6b" opacity="0.2" />
        <rect x="404" y="122" width="46" height="8" fill="#c98a2b" opacity="0.35" />
        <text x="404" y="143" fontSize="9" fontWeight="800" fill="#857e6e" fontFamily="inherit">kWh / EA</text>

        <rect x="386" y="176" width="116" height="74" fill="#fff" stroke="#c98a2b" strokeWidth="1.5" />
        <text x="404" y="202" fontSize="10" fontWeight="800" fill="#c98a2b" fontFamily="inherit">PEAK LOAD</text>
        <path d="M406 236l16-10 16 6 18-17 24 7" className="fems-svg-peak-line" fill="none" stroke="#c98a2b" strokeWidth="2" pathLength="1" />
        <line x1="404" y1="221" x2="488" y2="221" stroke="#c98a2b" strokeOpacity="0.16" strokeWidth="1.4" strokeDasharray="3 4" />

        <rect x="386" y="272" width="116" height="56" fill="#fff" stroke="#2c6f9f" strokeWidth="1.5" />
        <text x="404" y="303" fontSize="10" fontWeight="800" fill="#2c6f9f" fontFamily="inherit">CO2 REPORT</text>
        <path d="M468 303c8-16 16-17 19-17-1 10-4 19-19 17z" fill="none" stroke="#2c6f9f" strokeWidth="1.7" />
      </g>

      <rect x="196" y="338" width="148" height="32" fill="#fff" stroke="#2f8f6b" strokeWidth="1.6" />
      <text x="270" y="358" textAnchor="middle" dominantBaseline="middle" fontSize="17" fontWeight="800" fill="#2f8f6b" fontFamily="inherit">
        FEMS
      </text>
    </svg>
  );
}

function CapabilityCard({ item }) {
  const Icon = item.Icon;

  return (
    <article className="fems-cap-card">
      <span className="fems-cap-num">{item.num}</span>
      <Icon size={28} color="#2f8f6b" strokeWidth={1.8} aria-hidden="true" />
      <p className="fems-cap-category">{item.category}</p>
      <h3 className="fems-cap-title">{item.title}</h3>
      <p className="fems-cap-desc">{item.desc}</p>
    </article>
  );
}

function FEMSDashboard() {
  return (
    <div className="fems-dashboard" aria-label="FEMS 에너지 대시보드">
      <div className="fems-dashboard-top">
        <div>
          <p className="fems-dash-kicker">PLANT ENERGY</p>
          <h3>에너지 사용 현황</h3>
        </div>
        <span className="fems-live-badge"><Activity size={13} /> LIVE</span>
      </div>

      <div className="fems-metric-grid">
        {ENERGY_METRICS.map((metric) => (
          <div key={metric.label} className={`fems-metric is-${metric.tone}`}>
            <span>{metric.label}</span>
            <strong>{metric.value}</strong>
          </div>
        ))}
      </div>

      <div className="fems-energy-list">
        {ENERGY_STREAMS.map((item) => {
          const Icon = item.Icon;
          return (
            <div key={item.label} className="fems-energy-row">
              <span><Icon size={15} /> {item.label}</span>
              <div className="fems-energy-track">
                <i style={{ width: item.value }} />
              </div>
              <strong>{item.value}</strong>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WithFEMSHero() {
  const specs = [
    { label: 'METER', value: '전력 · 가스 · 스팀 · 용수' },
    { label: 'ANALYTICS', value: '원단위 · 피크 · 에너지 밸런스' },
    { label: 'ESG', value: '비용 절감 · 탄소 배출 · 리포트' },
  ];

  return (
    <section className="fems-hero">
      <div className="container">
        <div className="fems-hero-inner">
          <div className="fems-hero-diagram">
            <FEMSEnergyFlowDiagram />
          </div>

          <div className="fems-hero-text">
            <p className="fems-eyebrow">— FACTORY ENERGY MANAGEMENT SYSTEM</p>
            <h1 className="fems-hero-title">
              공장의 에너지를 <em className="fems-accent">한 흐름으로</em>읽다.
            </h1>
            <p className="fems-hero-desc">
              <strong>FEMS</strong>는 공장 내 전력·가스·스팀·용수 사용량을 실시간으로 수집하고,
              생산 정보와 연결해 에너지 원단위, 피크 부하, 탄소 배출량을 분석하는 공장에너지관리시스템입니다.
            </p>

            <div className="fems-specs">
              {specs.map((sp) => (
                <div key={sp.label} className="fems-spec-row">
                  <span className="fems-spec-label">{sp.label}</span>
                  <span className="fems-spec-value">{sp.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FEMSCapabilities() {
  return (
    <section className="fems-caps">
      <div className="container">
        <div className="fems-caps-header">
          <p className="fems-eyebrow">CORE CAPABILITIES</p>
          <h2 className="fems-caps-title">
            에너지 관리에 필요한 <em className="fems-accent">네 가지 시야</em>
          </h2>
          <p className="fems-caps-intro">
            FEMS는 단순 검침을 넘어, 설비·공정·제품 단위의 에너지 데이터를 분석하고 최적 제어와 ESG 보고까지 연결합니다.
          </p>
        </div>

        <div className="fems-caps-grid">
          {CAPABILITIES.map((cap) => (
            <CapabilityCard key={cap.num} item={cap} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FEMSDashboardSection() {
  return (
    <section className="fems-proposal fems-proposal-dashboard">
      <div className="container">
        <div className="fems-proposal-inner">
          <div className="fems-proposal-text">
            <p className="fems-eyebrow">ENERGY CONTROL ROOM</p>
            <h2 className="fems-proposal-title">
              피크는 낮추고, <em className="fems-accent">원단위는 더 정밀하게</em>
            </h2>
            <p className="fems-proposal-desc">
              전력 피크, 공정별 사용량, 에너지 원단위, 탄소 배출량을 한 화면에 배치해 낭비 구간과 절감 우선순위를 빠르게 찾습니다.
            </p>

            <div className="fems-value-list">
              <div><BarChart3 size={18} /> 에너지원별 사용 비중과 비용 추적</div>
              <div><AlertTriangle size={18} /> 이상 사용량과 피크 부하 알림</div>
              <div><CloudSun size={18} /> 탄소 배출량과 ESG 보고 기반 확보</div>
            </div>
          </div>

          <FEMSDashboard />
        </div>
      </div>
    </section>
  );
}

function FEMSProcessSection() {
  return (
    <section className="fems-proposal fems-proposal-process">
      <div className="container">
        <div className="fems-process-head">
          <p className="fems-eyebrow">ENERGY MANAGEMENT FLOW</p>
          <h2 className="fems-proposal-title">
            계측부터 보고까지 <em className="fems-accent">하나의 에너지 흐름</em>으로
          </h2>
          <p className="fems-proposal-desc">
            에너지 계측 데이터와 생산 데이터를 함께 분석해 비용 절감, 피크 억제, 탄소 배출 관리까지 이어지는 운영 체계를 구성합니다.
          </p>
        </div>

        <div className="fems-process-steps">
          <div className="fems-process-line" aria-hidden="true" />
          {PROCESS_STEPS.map((step) => (
            <article key={step.n} className="fems-process-step">
              <span className="fems-process-num">{step.n}</span>
              <h3>{step.title}</h3>
              <p>{step.desc}</p>
            </article>
          ))}
        </div>

        <div className="fems-before-after">
          <div className="fems-compare-col">
            <p className="fems-compare-label">Before</p>
            <h3>총량 중심의 사후 검침</h3>
            <ul>
              <li>에너지 사용량과 생산량 분리 관리</li>
              <li>피크 발생 원인 추적 지연</li>
              <li>탄소·비용 리포트 수작업 작성</li>
            </ul>
          </div>
          <div className="fems-compare-arrow" aria-hidden="true">→</div>
          <div className="fems-compare-col is-after">
            <p className="fems-compare-label">After</p>
            <h3>공정 단위의 실시간 에너지 관리</h3>
            <ul>
              <li>설비·라인별 에너지 원단위 분석</li>
              <li>피크 부하와 이상 사용량 조기 감지</li>
              <li>ESG·탄소 배출 리포트 자동화</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function FEMSCTA() {
  return (
    <section className="fems-cta">
      <div className="container">
        <div className="fems-launch-note">
          <p className="fems-eyebrow">COMING SOON</p>
          <h2 className="fems-cta-title">
            <em className="fems-accent">2027년</em> 정식 런칭 예정
          </h2>
        </div>
      </div>
    </section>
  );
}

export default function WithFEMSOverview() {
  return (
    <>
      <WithFEMSHero />
      <FEMSCapabilities />
      <FEMSDashboardSection />
      <FEMSProcessSection />
      <FEMSCTA />
    </>
  );
}
